/**
 * BED — Ouverture d'édition (migration 00293, TICKET-BED-22).
 *
 * Prépare le lien personnel du bulletin de chaque équipage pour la newsletter
 * d'ouverture, puis l'exporte pour Mailjet. Même discipline que la clôture
 * d'édition : aperçu chiffré, confirmation explicite, exécution serveur.
 *
 *   1. aperçu : qui recevra un lien (vague 1), qui en est exclu et pourquoi ;
 *   2. « Préparer les liens » : émet ou prolonge les liens par lots, jusqu'à
 *      la date de validité (par défaut le lendemain de la date butoir du
 *      solde) ;
 *   3. « Exporter » : une ligne par (email, équipage), lien complet construit
 *      avec l'origine du site 4L Trophy.
 *
 * Les équipages exclus (dérogation d'âge, preuve de BPI manquante…) se
 * traitent fiche par fiche.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, Link2, RefreshCw } from 'lucide-react';
import { Button, ConfirmDialog, Input, Select, toast } from '../ui';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { BedSiteOriginMissingError } from '../../lib/bed/admin';
import {
  bedOpeningErrorMessage,
  buildBedOpeningCsvRows,
  exportBedOpening,
  generateBedOpeningBatch,
  previewBedOpening,
  type BedOpeningListEntry,
  type BedOpeningPreview,
} from '../../lib/bed/opening';
import { ROUTES } from '../../lib/routes/constants';
import { exportToCSV } from '../../lib/csv';
import { isValidEmailAddress } from '../../schemas/email';

function formatDateFr(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
}

// Recette du 07/10 : les motifs seuls ne disaient ni ce qu'ils voulaient dire,
// ni quoi faire pour remettre l'équipage dans la vague 1.
const EXCLUSION_REASONS: {
  key: 'excluded_derogation' | 'no_proof' | 'member_paid' | 'other_edition' | 'conflicting_amounts';
  /** Motif renvoyé dans `exclusions` (00298). */
  reason: string;
  label: string;
  help: string;
}[] = [
  {
    key: 'excluded_derogation',
    reason: 'derogation',
    label: "Dérogation d'âge non validée",
    help: "Un membre aura 29 ans ou plus au départ. Une fois l'accord donné, cochez « Dérogation validée » sur sa carte Pilote ou Copilote (fiche équipage), puis cliquez « Recalculer ».",
  },
  {
    key: 'no_proof',
    reason: 'none',
    label: 'Sans preuve de BPI',
    help: "Ni BPI payé dans le module, ni acompte validé à la main. Si l'acompte a été réglé ailleurs, utilisez « Valider le BPI manuellement » sur la fiche équipage.",
  },
  {
    key: 'member_paid',
    reason: 'member_paid',
    label: "BPI réglé par un membre, pas par l'équipage",
    help: "Le BPI a été payé au nom du pilote ou du copilote, sans être rattaché à l'équipage : le CRM ne peut pas le lui attribuer. À signaler à l'équipe technique (rattachement).",
  },
  {
    key: 'other_edition',
    reason: 'other_edition',
    label: 'BPI réglé sur une autre édition',
    help: "Le dernier BPI payé de l'équipage est sur une autre édition (report) : le lien ouvrirait le bulletin de cette autre édition. À signaler à l'équipe technique.",
  },
  {
    key: 'conflicting_amounts',
    reason: 'conflicting_amounts',
    label: 'Attestations de montants différents',
    help: "Pilote et copilote ont chacun un acompte validé à la main, de montants différents. Validez l'acompte directement sur l'équipage pour trancher.",
  },
];

/**
 * Équipages concernés par un motif (00298), chacun avec un lien vers sa fiche
 * dans un nouvel onglet : le staff garde l'écran d'ouverture. Rien sans liste
 * (base pas encore migrée).
 */
function CrewEntries({ entries }: { entries: BedOpeningListEntry[] | undefined }) {
  if (!entries || entries.length === 0) return null;
  return (
    <details className="mt-1">
      <summary className="cursor-pointer text-xs text-zinc-600 underline underline-offset-2">
        Voir {entries.length > 1 ? `les ${entries.length} équipages` : 'l’équipage'}
      </summary>
      <ul className="mt-1 max-h-48 overflow-y-auto text-xs">
        {entries.map((entry) => (
          <li key={entry.crew_id}>
            <a
              href={ROUTES.DATA.CREWS.DETAIL(entry.crew_id)}
              target="_blank"
              rel="noreferrer"
              className="text-zinc-700 underline underline-offset-2 hover:text-zinc-900"
            >
              {entry.crew_number ?? 'sans numéro'} · {entry.crew_name ?? 'sans nom'}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function byReason(entries: BedOpeningListEntry[] | undefined, reason: string) {
  return entries?.filter((entry) => entry.reason === reason);
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
      <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-zinc-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export function BedOpeningCard() {
  const { products, loading: productsLoading } = useProductsSelect();
  const [productId, setProductId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [preview, setPreview] = useState<BedOpeningPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const loadPreview = useCallback(async (id: string, until: string) => {
    if (!id) {
      setPreview(null);
      return;
    }
    setLoading(true);
    const res = await previewBedOpening(id, until || null);
    if (res.status === 'ok') {
      setPreview(res.data);
    } else {
      setPreview(null);
      toast.error(bedOpeningErrorMessage(res.status));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setProgress(null);
    void loadPreview(productId, validUntil);
  }, [productId, validUntil, loadPreview]);

  async function handleGenerate() {
    setConfirmOpen(false);
    setLoading(true);
    let after: string | null = null;
    let created = 0;
    let extended = 0;
    const errors: Record<string, number> = {};
    // Lots de 100 : une requête reste sous le délai de PostgREST. Le serveur
    // saute un équipage en erreur ; le curseur garantit qu'on avance.
    for (;;) {
      const res = await generateBedOpeningBatch(productId, validUntil || null, after);
      if (res.status !== 'ok') {
        toast.error(bedOpeningErrorMessage(res.status));
        break;
      }
      created += res.data.created;
      extended += res.data.extended;
      for (const [code, count] of Object.entries(res.data.errors)) {
        errors[code] = (errors[code] ?? 0) + count;
      }
      setProgress(`${created} lien(s) créé(s), ${extended} prolongé(s)…`);
      after = res.data.next_after_crew_id;
      if (res.data.done) {
        const skipped = Object.values(errors).reduce((sum, n) => sum + n, 0);
        const summary =
          `${created} lien(s) créé(s), ${extended} prolongé(s)` +
          (skipped > 0
            ? `, ${skipped} équipage(s) sauté(s) (${Object.keys(errors).join(', ')})`
            : '');
        setProgress(summary);
        if (skipped > 0) toast.warning(summary);
        else toast.success(summary);
        break;
      }
    }
    await loadPreview(productId, validUntil);
    setLoading(false);
  }

  async function handleExport() {
    setLoading(true);
    const res = await exportBedOpening(productId, validUntil || null);
    setLoading(false);
    if (res.status !== 'ok') {
      toast.error(bedOpeningErrorMessage(res.status));
      // Un lien a été révoqué ou a expiré depuis l'aperçu : le rafraîchir
      // rebloque l'export et affiche ce qui manque.
      if (res.status === 'wave_incomplete') await loadPreview(productId, validUntil);
      return;
    }
    if (res.data.length === 0) {
      toast.error('Aucun lien actif à exporter : préparez d’abord les liens.');
      return;
    }
    try {
      const rows = buildBedOpeningCsvRows(res.data);
      exportToCSV(rows, 'liens-bulletin', { silentEmpty: true });
      const shared = rows.filter((r) => r.email_sur_plusieurs_equipages === 'oui').length;
      toast.success(
        `${rows.length} ligne(s) exportée(s)` +
          (shared > 0 ? ` — ${shared} ligne(s) avec une adresse sur plusieurs équipages` : '')
      );
      // Recette du 07/10 : une adresse invalide (ex. « ff ») partirait telle
      // quelle vers la liste Mailjet de la newsletter.
      const invalid = rows.filter((r) => !isValidEmailAddress(r.email));
      if (invalid.length > 0) {
        toast.warning(`${invalid.length} adresse(s) invalide(s) dans l’export`, {
          description: `Équipage(s) ${[...new Set(invalid.map((r) => r.numero_equipage || r.nom_equipage))].join(', ')} : corrigez l’adresse sur la fiche du contact, puis exportez à nouveau.`,
          duration: 15000,
        });
      }
    } catch (err) {
      toast.error(
        err instanceof BedSiteOriginMissingError
          ? 'L’adresse du site 4L Trophy n’est pas configurée : export impossible.'
          : 'Une erreur est survenue.'
      );
    }
  }

  const toPrepare = (preview?.to_create ?? 0) + (preview?.to_extend ?? 0);
  // L'export n'a de sens que complet : tant qu'un lien manque ou expire trop
  // tôt, la newsletter oublierait des équipages.
  const exportReady = !!preview?.pricing_confirmed && preview.link_ok > 0 && toPrepare === 0;

  return (
    <section className="flex flex-col gap-4 px-page-padding py-6">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900">
          <Link2 className="size-4 text-zinc-500" aria-hidden />
          Ouverture du BED
        </h2>
        <p className="max-w-3xl text-sm leading-5 text-zinc-500">
          Prépare le lien personnel du bulletin de chaque équipage (BPI réglé ou validé, sans
          dérogation d&apos;âge en attente), valable jusqu&apos;à la date choisie, puis
          l&apos;exporte pour la newsletter. Les équipages exclus se traitent depuis leur fiche.
          Aucun email n&apos;est envoyé par le CRM.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Édition</span>
          <Select
            className="w-72"
            value={productId}
            disabled={productsLoading || loading}
            onChange={(e) => setProductId(e.target.value)}
            options={products.map((p) => ({ value: p.id, label: p.name }))}
            placeholder="— choisir une édition —"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-zinc-600">Liens valables jusqu&apos;au</span>
          <Input
            type="date"
            className="w-44"
            value={validUntil}
            disabled={loading}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </label>
        <Button
          variant="outline"
          disabled={!productId || loading}
          onClick={() => loadPreview(productId, validUntil)}
        >
          <RefreshCw className="size-4" aria-hidden />
          Recalculer
        </Button>
        <Button
          disabled={!productId || loading || !preview?.pricing_confirmed || toPrepare === 0}
          onClick={() => setConfirmOpen(true)}
        >
          <Link2 className="size-4" aria-hidden />
          Préparer les liens
        </Button>
        <Button
          variant="outline"
          disabled={!productId || loading || !exportReady}
          onClick={handleExport}
        >
          <Download className="size-4" aria-hidden />
          Exporter pour la newsletter
        </Button>
      </div>

      {preview && (
        <div className="flex flex-col gap-3">
          {!preview.pricing_confirmed && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              La grille tarifaire de cette édition n&apos;est pas confirmée : un bulletin ouvert
              maintenant n&apos;afficherait aucun montant. Aucun lien ne peut être préparé.
            </p>
          )}
          <p className="text-sm text-zinc-600">
            Liens valables jusqu&apos;au <strong>{formatDateFr(preview.valid_until)}</strong>
            {preview.balance_deadline &&
              ` (date butoir du solde : ${formatDateFr(preview.balance_deadline)})`}
            .
          </p>
          {/* Recette du 07/10 : une date saisie pour essayer avait produit des
              liens valables jusqu'au lendemain seulement. */}
          {validUntil && preview.balance_deadline && validUntil < preview.balance_deadline && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Cette date tombe avant la fin de la période de paiement du solde : les liens créés ou
              prolongés maintenant expireront avant que les équipages aient fini de payer. Pour
              l&apos;ouverture, videz le champ date : validité recommandée jusqu&apos;au lendemain
              de la date butoir (un lien déjà valable plus longtemps n&apos;est jamais raccourci).
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Vague 1" value={preview.eligible} hint="équipages qui recevront un lien" />
            <Stat label="Liens prêts" value={preview.link_ok} hint="valables jusqu'à la date" />
            <Stat label="À créer" value={preview.to_create} />
            <Stat label="À prolonger" value={preview.to_extend} />
          </div>

          <div className="rounded-md border border-zinc-200 p-4">
            <p className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
              Exclus de la vague 1 (à traiter depuis la fiche équipage)
            </p>
            <ul className="mt-2 flex flex-col gap-2 text-sm text-zinc-600">
              {EXCLUSION_REASONS.map((reason) => (
                <li key={reason.key}>
                  {reason.label} :{' '}
                  <span className="font-semibold text-zinc-900">{preview[reason.key]}</span>
                  <span className="block text-xs text-zinc-500">{reason.help}</span>
                  <CrewEntries entries={byReason(preview.exclusions, reason.reason)} />
                </li>
              ))}
            </ul>
          </div>

          {(preview.manual_not_400 > 0 ||
            preview.birth_date_missing > 0 ||
            preview.eligible_without_email > 0) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">À vérifier dans la vague 1</p>
              <ul className="mt-1 list-inside list-disc">
                {preview.manual_not_400 > 0 && (
                  <li>
                    {preview.manual_not_400} acompte(s) validé(s) à la main d&apos;un autre montant
                    que 400 €
                    <CrewEntries entries={byReason(preview.to_check, 'manual_not_400')} />
                  </li>
                )}
                {preview.birth_date_missing > 0 && (
                  <li>
                    {preview.birth_date_missing} équipage(s) avec une date de naissance manquante
                    (âge non contrôlé)
                    <CrewEntries entries={byReason(preview.to_check, 'birth_date_missing')} />
                  </li>
                )}
                {preview.eligible_without_email > 0 && (
                  <li>
                    {preview.eligible_without_email} équipage(s) sans aucune adresse email : absents
                    de l&apos;export, lien à transmettre depuis la fiche
                    <CrewEntries entries={byReason(preview.to_check, 'no_email')} />
                  </li>
                )}
              </ul>
            </div>
          )}

          {preview.pricing_confirmed && toPrepare > 0 && (
            <p className="text-sm text-zinc-500">
              Étape suivante : « Préparer les liens » ({preview.to_create} à créer,{' '}
              {preview.to_extend} à prolonger). L&apos;export se débloque quand tous les liens de la
              vague 1 sont prêts.
            </p>
          )}

          {progress && <p className="text-sm text-zinc-600">{progress}</p>}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Préparer les liens du bulletin ?"
        description={
          preview
            ? `${preview.to_create} lien(s) seront créés et ${preview.to_extend} prolongés, valables jusqu'au ${formatDateFr(preview.valid_until)}. Aucun email n'est envoyé : les liens partent avec la newsletter, après l'export.`
            : ''
        }
        confirmLabel="Préparer les liens"
        onConfirm={handleGenerate}
      />
    </section>
  );
}
